import { useCallback, useEffect, useState } from "react";
import { pdf } from "@react-pdf/renderer";
import { getUrl } from "aws-amplify/storage";
import { useUpload, useUserData } from "hooks";
import { capitalizeExceptCommonWords, sanitizeApplicationId } from "services";
import { DocumentType, OfferCondition, offerConditions } from "types";

interface UseOfferLetterOptions {
  details: any;
  documentType: DocumentType;
  onDocumentLinked?: () => void;
}

const ALLOWED_KEYS: Record<string, Set<string>> = {
  extractedData: new Set(["documents"]),
};

export const useOfferLetter = ({
  details,
  documentType,
  onDocumentLinked,
}: UseOfferLetterOptions) => {
  const userDataHook = useUserData();

  const { handleFileUpload, handleError } = useUpload(
    userDataHook,
    ALLOWED_KEYS,
    {
      enableExtraction: false,
      customUploadMessage: "Offer letter uploaded and linked successfully.",
      documentContext: {
        linkedEntityId: details?.applicationId,
        linkedEntityType: "APPLICATION",
        allowMultiple: false,
        institutionId: details.courseData.institutionId,
      },
    },
  );

  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const allConditions = offerConditions.map((condition) => condition.value);
  const [selectedConditions, setSelectedConditions] =
    useState<string[]>(allConditions);

  const [isLetterGenerated, setIsLetterGenerated] = useState(false);

  const [logo, setLogo] = useState<string | null>(null);

  useEffect(() => {
    const fetchLogo = async () => {
      if (!details?.courseData?.institutionId) return;

      try {
        const { url } = await getUrl({
          path: `public/${details.courseData.institutionId}/favicon.png`,
          options: {
            bucket: "InstitutionBucket",
          },
        });

        const response = await fetch(url.href);
        const blob = await response.blob();

        const reader = new FileReader();
        reader.onloadend = () => {
          setLogo(reader.result as string);
        };
        reader.readAsDataURL(blob);
      } catch (error) {
        console.error("Failed to fetch institution logo:", error);
      }
    };

    fetchLogo();
  }, [details?.courseData?.institutionId]);

  const filetype =
    documentType === DocumentType.CONDITIONAL_OFFER_LETTER
      ? "conditional"
      : documentType === DocumentType.UNCONDITIONAL_OFFER_LETTER
        ? "unconditional"
        : (() => {
            throw new Error(`Unsupported document type: ${documentType}`);
          })();

  const filename = `${capitalizeExceptCommonWords(filetype)}_OfferLetter_${sanitizeApplicationId(details.applicationId)}.pdf`;

  const generateFile = useCallback(
    async (pdfComponent: React.ReactElement): Promise<File> => {
      const blob = await pdf(pdfComponent).toBlob();
      return new File([blob], filename, { type: "application/pdf" });
    },
    [filename],
  );

  const handleToggleConditions = useCallback(
    (value: string) => {
      setSelectedConditions((prev) => {
        if (value === OfferCondition.ALL) {
          return prev.length === allConditions.length ? [] : allConditions;
        }

        const isSelected = prev.includes(value);
        const updated = isSelected
          ? prev.filter((v) => v !== value)
          : [...prev, value];

        const baseConditions = allConditions.filter(
          (c) => c !== OfferCondition.ALL,
        );
        const hasAllSelected = baseConditions.every((c) => updated.includes(c));

        return hasAllSelected
          ? allConditions
          : updated.filter((c) => c !== OfferCondition.ALL);
      });
    },
    [allConditions],
  );

  const handleGenerateLetter = useCallback(async () => {
    if (!selectedConditions.length) {
      return handleError("Please select offer letter conditions.");
    }
    setIsLetterGenerated(true);
  }, [selectedConditions, handleError]);

  const handleSubmit = useCallback(
    async (pdfComponent: React.ReactElement) => {
      if (!details?.applicationId) {
        return handleError("Application ID missing.");
      }

      setIsLoading(true);

      try {
        const file = await generateFile(pdfComponent);

        // The LinkedDocumentUploadStrategy will automatically:
        // 1. Upload the file
        // 2. Create Document record with version tracking
        // 3. Create DocumentLink to the application
        const { results } = await handleFileUpload([
          {
            file,
            fileType: `${filetype}offerletter`,
            progress: 0,
            stage: "idle",
            applicationId: details.applicationId,
          },
        ]);

        if (!results.length) {
          return handleError("Upload failed.");
        }

        setSuccessMessage("Offer letter uploaded and linked successfully.");
        onDocumentLinked?.();
      } catch (error) {
        console.error(error);
        handleError("Failed to submit offer letter.");
      } finally {
        setIsLoading(false);
      }
    },
    [
      details?.applicationId,
      generateFile,
      handleFileUpload,
      handleError,
      onDocumentLinked,
      filetype,
    ],
  );

  return {
    // State
    isLoading,
    successMessage,
    filename,
    isLetterGenerated,
    selectedConditions,
    logoBase64: logo,
    allConditions,

    // Actions
    setIsLetterGenerated,
    handleToggleConditions,
    handleGenerateLetter,
    handleSubmit,

    // Utils
    handleError,
  };
};
